import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
import deDe from 'antd/lib/locale/de_DE';
import enUS from 'antd/lib/locale/en_US';
import GlobalStyle from './styles/GlobalStyle';
import 'typeface-montserrat';
import 'typeface-lato';
import { AppRouter } from './components/router/AppRouter';
import { useLanguage } from './hooks/useLanguage';
import { useAutoNightMode } from './hooks/useAutoNightMode';
import { usePWA } from './hooks/usePWA';
import { useThemeWatcher } from './hooks/useThemeWatcher';
import { useAppDispatch, useAppSelector } from './hooks/reduxHooks';
import { themeObject } from './styles/themes/themeVariables';
import { fetchLoginUserById } from './store/slices/userSlice';
import { updateGlobalLoading } from './store/slices/GlobalLoadingSlice';
import { GlobalLoading } from './components/common/Loading/GlobalLoading';
// import { io } from 'socket.io-client';
import useSocket from './hooks/useSocket';
import { notificationController } from './controllers/notificationController';
import { globalNavigate } from './controllers/navigationController';

// const socket = io('http://localhost:8080');

const App: React.FC = () => {
  const { language } = useLanguage();

  const theme = useAppSelector((state) => state.theme.theme);
  const globalLoading = useAppSelector((state) => state.globalLoading.loading);
  usePWA();

  useAutoNightMode();

  useThemeWatcher();
  const dispatch = useAppDispatch();
  const id = useAppSelector((state) => state.auth.token?.id);

  const { socket } = useSocket(process.env.REACT_APP_BASE_URL || '');

  useEffect(() => {
    if (socket) {
      // Send data or listen to events on the socket
      socket.current.emit('connection', { data: 'from client' });

      // eslint-disable-next-line
      socket.current.on('task-data-changed', (data: any) => {
        notificationController.info({ message: data.message });
      });
    }
  }, [socket]);

  useEffect(() => {
    // socket.emit('connection');
    if (id) {
      dispatch(updateGlobalLoading(true));
      dispatch(fetchLoginUserById(id || 0))
        .unwrap()
        .then(() => {
          dispatch(updateGlobalLoading(false));
        })
        .catch(() => {
          dispatch(updateGlobalLoading(false));
          globalNavigate('/auth/login');
        });
    }
    // eslint-disable-next-line
  }, [id]);

  return (
    <>
      {globalLoading ? (
        <>
          <GlobalLoading />
        </>
      ) : (
        <>
          <meta name="theme-color" content={themeObject[theme].primary} />
          <GlobalStyle />
          <HelmetProvider>
            <ConfigProvider locale={language === 'en' ? enUS : deDe}>
              <AppRouter />
            </ConfigProvider>
          </HelmetProvider>
        </>
      )}
    </>
  );
};

export default App;
